// DOM-free helpers shared by every notification surface (bell, inbox, admin bell, push click).
// Phase 46, plan 46-18. Kept free of React and browser globals so it is SSR-safe and unit-tested.

import { isSafeRelativePath, type NotificationAudience } from "@shared/notifications";
import { NOTIFICATION_DATE_GROUPS, groupLabelFor, toArabicNumber } from "@shared/notificationCopy";

// D-13: the unread badge polls once a minute and is considered fresh for 30 seconds.
export const POLL_INTERVAL_MS = 60_000;
export const UNREAD_STALE_MS = 30_000;

const BADGE_CAP = 9;
const FALLBACK_LINK = "/notifications";

/**
 * The search string without `?token=` (the signed email-link token), "" when nothing is left.
 * The unsubscribe / verify-email pages read the token into state, then replaceState with this so
 * it does not linger in the address bar, analytics URLs or session replay.
 */
export const stripTokenParam = (search: string): string => {
  const params = new URLSearchParams(search);
  params.delete("token");
  const rest = params.toString();
  return rest ? `?${rest}` : "";
};

/**
 * Browser-only: removes `token` from the current URL in place. Call from an effect.
 */
export const stripTokenFromLocation = (): void => {
  if (typeof window === "undefined") return;
  const { pathname, search, hash } = window.location;
  if (!new URLSearchParams(search).has("token")) return;
  window.history.replaceState(window.history.state, "", pathname + stripTokenParam(search) + hash);
};

/** Badge text: null (hidden) at 0, «١»..«٩», then «+٩». */
export const formatBadgeCount = (count: number): string | null => {
  if (!Number.isFinite(count) || count <= 0) return null;
  if (count > BADGE_CAP) return `+${toArabicNumber(BADGE_CAP)}`;
  return toArabicNumber(Math.floor(count));
};

/**
 * D-09: a deep link from the server is only followed when it is a same-origin relative path.
 * Anything else (protocol-relative, absolute, javascript:, empty) lands on the inbox instead.
 */
export const safeDeepLink = (link: string | null | undefined): string => {
  if (typeof link !== "string") return FALLBACK_LINK;
  return isSafeRelativePath(link) ? link : FALLBACK_LINK;
};

export type NotificationDateGroupKey = keyof typeof NOTIFICATION_DATE_GROUPS;

export type NotificationDateGroup<T> = {
  key: NotificationDateGroupKey;
  label: string;
  items: T[];
};

const GROUP_ORDER: NotificationDateGroupKey[] = ["today", "yesterday", "thisWeek", "older"];

const keyForLabel = (label: string): NotificationDateGroupKey =>
  GROUP_ORDER.find((k) => NOTIFICATION_DATE_GROUPS[k] === label) ?? "older";

/**
 * Buckets rows into today / yesterday / this week / older (Cairo days), in that order, omitting
 * empty groups. Item order within a group is preserved (the API already sorts newest first).
 */
export const groupNotificationsByDate = <T extends { lastEventAt: Date | string }>(
  items: T[],
  now: Date = new Date(),
): NotificationDateGroup<T>[] => {
  const buckets: Record<NotificationDateGroupKey, T[]> = { today: [], yesterday: [], thisWeek: [], older: [] };
  for (const item of items) {
    buckets[keyForLabel(groupLabelFor(item.lastEventAt, now))].push(item);
  }
  return GROUP_ORDER.filter((k) => buckets[k].length > 0).map((k) => ({
    key: k,
    label: NOTIFICATION_DATE_GROUPS[k],
    items: buckets[k],
  }));
};

/** TanStack Query keys. Audience-scoped so the public and admin bells never share a cache. */
export const NOTIFICATION_QUERY_KEYS = {
  all: ["notifications"] as const,
  unreadCount: (audience: NotificationAudience) => ["notifications", "unread-count", audience] as const,
  list: (audience: NotificationAudience) => ["notifications", "list", audience] as const,
  preferences: ["notifications", "preferences"] as const,
};
